import type { AlertHookSummaryField } from '@cobliteam/hooks/src/useAlertHooks/alertHookConfig';
import { useTranslation } from '@cobliteam/i18n';
import { Button, Dialog, Typography } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import SummaryRow from './SummaryRow';

interface AlertHookConfirmLayoutProps {
  iconName: string;
  iconBg: string;
  title: string;
  description: string;
  rows: AlertHookSummaryField[];
  editLabel: string;
  onEdit: () => void;
  onActivate: () => void;
  isActivating: boolean;
  onClose: () => void;
}

/** Step 2 body shared by both activation paths: the preview card, the edit link and the footer. */
const AlertHookConfirmLayout = ({ iconName, iconBg, title, description, rows, editLabel, onEdit, onActivate, isActivating, onClose }: AlertHookConfirmLayoutProps) => {
  const { t } = useTranslation(['alertHooks']);

  return (
    <Dialog.Content size='sm' className='flex flex-col gap-0 p-0'>
      <Dialog.Body className='px-6 pt-2 pb-4'>
        <div className='mb-4 flex flex-col items-center justify-center pt-8 text-center'>
          <div className={`flex h-14 w-14 items-center justify-center rounded-full ${iconBg}`}>
            <Icon name={iconName as never} size='lg' color='primary' />
          </div>
          <Dialog.Title className='mt-4 text-center'>{title}</Dialog.Title>
          <Dialog.Description asChild>
            <Typography variant='text-sm' color='secondary' className='mt-2 px-6 text-center'>
              {description}
            </Typography>
          </Dialog.Description>
        </div>

        <div className='overflow-hidden rounded-2xl border border-gray-6'>
          {rows.map((row, index) => (
            <SummaryRow key={row.labelKey} label={t(row.labelKey)} value={t(row.valueKey, row.valueParams)} withDivider={index < rows.length - 1} />
          ))}
          <button
            type='button'
            onClick={onEdit}
            className='flex w-full items-center justify-center gap-2 border-gray-6 border-t bg-white px-4 py-3 text-primary hover:cursor-pointer hover:bg-gray-2'
          >
            <Icon name='pencil' size='sm' />
            <Typography variant='text-sm-medium' color='primary'>
              {editLabel}
            </Typography>
            <Icon name='external-link' size='sm' />
          </button>
        </div>
      </Dialog.Body>

      <Dialog.Footer className='flex flex-col gap-2 px-6 pt-4'>
        <Button onClick={onActivate} disabled={isActivating} className='w-full'>
          {t('alertHooks:common.ctaActivate')}
        </Button>
        <Button onClick={onClose} variant='ghost' className='w-full'>
          {t('alertHooks:common.ctaClose')}
        </Button>
      </Dialog.Footer>
    </Dialog.Content>
  );
};

export default AlertHookConfirmLayout;
